import styled from "@emotion/styled";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
  linearProgressClasses
} from "@mui/material";
import { Button } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { assetSchema, useAssetsStore, useCreateAsset } from "features/assets";
import { useChurchStore } from "features/churches";
import moment from "moment";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BsXSquare } from "react-icons/bs";
import { AssetModalSteps } from "../AssetModalSteps";
import { AcquisitionForm } from "../Forms/AcquisitionForm";
import { BasicInfoForm } from "../Forms/BasicInfoForm";
import { ComplianceForm } from "../Forms/ComplianceForm";
import { FinancialInfoForm } from "../Forms/FinancialInfoForm";

const CloseIcon = styled(BsXSquare)`
  color: #ffffff;
  height: 22px;
  width: 22px;
  font-weight: bold;
  margin-left: auto;
  cursor: pointer;
`;

const BorderLinearProgress = styled(LinearProgress)(() => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: "rgba(233, 233, 233, 1)"
  }
}));

export const AddAssetModal = ({ open, onClose }) => {
  const addAssetFormActiveStep = useAssetsStore(
    state => state.addAssetFormActiveStep
  );
  const setAddAssetFormActiveStep = useAssetsStore(
    state => state.setAddAssetFormActiveStep
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const { t } = useTranslation();

  const [documents, setDocuments] = useState([]);

  const createAssetMutation = useCreateAsset();

  const {
    register,
    formState: { errors, defaultValues },
    handleSubmit,
    reset,
    trigger
  } = useForm({
    defaultValues: {
      name: "",
      type: "",
      description: "",
      quantity: 0,
      acquisition_method: "",
      acquisition_date: new Date(),
      purchase_price: 0,
      vendor: "",
      assigned_to: "",
      depreciation_method: "",
      location: "",
      status: "",
      condition: "",
      depreciation_rate: 0,
      book_value: 0,
      maintenance_schedule: new Date(),
      last_maintenance_date: new Date(),
      warranty_expiry_date: new Date(),
      documents: null,
      compliance_status: "",
      notes: ""
    },
    mode: "onBlur",
    resolver: yupResolver(assetSchema)
  });

  // Define the fields that belong to each step
  const stepFields = {
    1: ["name", "type", "description", "quantity"],
    2: [
      "acquisition_method",
      "acquisition_date",
      "purchase_price",
      "vendor",
      "location",
      "assigned_to"
    ],
    3: [
      "status",
      "condition",
      "depreciation_method",
      "depreciation_rate",
      "book_value"
    ],
    4: [
      "maintenance_schedule",
      "last_maintenance_date",
      "warranty_expiry_date",
      "documents",
      "compliance_status",
      "notes"
    ]
  };

  // Find the step with validation errors
  const findStepWithErrors = () => {
    if (!errors || Object.keys(errors).length === 0) return null;

    for (const [step, fields] of Object.entries(stepFields)) {
      const hasError = fields.some(field => errors[field]);
      if (hasError) {
        return parseInt(step);
      }
    }
    return null;
  };

  // Handle next button click
  const handleNext = async () => {
    // Validate current step fields
    const currentStepFields = stepFields[addAssetFormActiveStep];
    const isValid = await trigger(currentStepFields);

    if (isValid) {
      setAddAssetFormActiveStep(addAssetFormActiveStep + 1);
    }
  };

  const forms = {
    1: <BasicInfoForm register={register} errors={errors} />,
    2: <AcquisitionForm register={register} errors={errors} />,
    3: <FinancialInfoForm register={register} errors={errors} />,
    4: (
      <ComplianceForm
        register={register}
        errors={errors}
        documents={documents}
        setDocuments={setDocuments}
      />
    )
  };

  const onSubmit = values => {
    const formData = new FormData();

    // Format date fields to YYYY-MM-DD format
    const dateFields = [
      "acquisition_date",
      "maintenance_schedule",
      "last_maintenance_date",
      "warranty_expiry_date"
    ];

    for (let key in values) {
      if (dateFields.includes(key) && values[key]) {
        formData.append(key, moment(values[key]).format("YYYY-MM-DD"));
      } else {
        formData.append(key, values[key]);
      }
    }

    if (documents.length !== 0) {
      for (const [index, document] of documents.entries()) {
        formData.append(`documents[${index}]`, document);
      }
      formData.delete("documents");
    } else {
      formData.delete("documents");
    }

    createAssetMutation.mutate(
      {
        churchId: selectedChurch?.id,
        assetData: formData
      },
      {
        onSuccess: () => {
          reset({
            ...defaultValues
          });
        }
      }
    );
  };

  // Wrapper for handleSubmit that checks for errors
  const handleFormSubmit = handleSubmit(data => {
    const stepWithErrors = findStepWithErrors();
    if (stepWithErrors) {
      // Navigate to the step with errors
      setAddAssetFormActiveStep(stepWithErrors);
    } else {
      // If no errors, proceed with form submission
      onSubmit(data);
    }
  });

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={() => {
        setAddAssetFormActiveStep(1);
        onClose();
      }}
    >
      <DialogTitle sx={{ bgcolor: "primary.main" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="subtitle1" color="primary.contrastText">
            {t("Asset.AssetRegistration.AssetRegistration")}
          </Typography>
          <CloseIcon
            onClick={() => {
              setAddAssetFormActiveStep(1);
              onClose();
            }}
          />
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack py={1} spacing={2}>
          <BorderLinearProgress
            variant="determinate"
            value={(addAssetFormActiveStep / 4) * 100}
          />
          <AssetModalSteps />
          <Stack px={3}>{forms[addAssetFormActiveStep]}</Stack>
          <MutationError error={createAssetMutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          flavor="text"
          onClick={onClose}
          disabled={createAssetMutation.isLoading}
        >
          {t("form.common.cancel")}
        </Button>
        {addAssetFormActiveStep !== 1 && (
          <Button
            flavor="outlined"
            onClick={() =>
              setAddAssetFormActiveStep(addAssetFormActiveStep - 1)
            }
            disabled={createAssetMutation.isLoading}
          >
            {t("form.common.back")}
          </Button>
        )}
        {addAssetFormActiveStep !== 4 && (
          <Button onClick={handleNext} disabled={createAssetMutation.isLoading}>
            {t("form.common.next")}
          </Button>
        )}
        {addAssetFormActiveStep === 4 && (
          <Button
            onClick={handleFormSubmit}
            disabled={createAssetMutation.isLoading}
          >
            {t("form.common.submit")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
